/**
 * Filter State Service
 *
 * Manages global filter state for the entire application.
 * Persists selected schools and date range across navigation.
 * Default: all schools selected, last 7 days date range.
 */

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { School } from '../models';
import { SchoolApiService } from '../../features/schools/services/school-api.service';

/**
 * Date range preset options
 */
export type DateRangePreset = 'last7days' | 'last30days' | 'lastYear' | 'custom';

/**
 * Filter state interface
 */
export interface FilterState {
  selectedSchoolIds: number[];
  datePreset: DateRangePreset;
  startDate: string;
  endDate: string;
  allSchools: School[];
  initialized: boolean;
}

/**
 * Calculate date range based on preset
 */
function calculateDateRange(preset: DateRangePreset): { startDate: string; endDate: string } {
  const yesterday = moment().subtract(1, 'day');
  const endDate = yesterday.format('YYYY-MM-DD');
  let startDate: string;

  switch (preset) {
    case 'last7days':
      startDate = yesterday.clone().subtract(6, 'days').format('YYYY-MM-DD'); // Yesterday + 6 days back = 7 days
      break;
    case 'last30days':
      startDate = yesterday.clone().subtract(29, 'days').format('YYYY-MM-DD'); // Yesterday + 29 days back = 30 days
      break;
    case 'lastYear':
      startDate = yesterday.clone().subtract(1, 'year').format('YYYY-MM-DD');
      break;
    default:
      startDate = yesterday.clone().subtract(6, 'days').format('YYYY-MM-DD');
  }

  return { startDate, endDate };
}

@Injectable({
  providedIn: 'root'
})
export class FilterStateService {
  private readonly schoolApiService = inject(SchoolApiService);

  /**
   * Initial date range (last 7 days)
   */
  private readonly initialDateRange = calculateDateRange('last7days');

  /**
   * Internal state subject
   */
  private readonly state$ = new BehaviorSubject<FilterState>({
    selectedSchoolIds: [],
    datePreset: 'last7days',
    startDate: this.initialDateRange.startDate,
    endDate: this.initialDateRange.endDate,
    allSchools: [],
    initialized: false
  });

  /**
   * Observable of the full filter state
   */
  readonly filterState$: Observable<FilterState> = this.state$.asObservable();

  /**
   * Observable of selected school IDs
   */
  readonly selectedSchoolIds$: Observable<number[]> = this.state$.pipe(
    map(state => state.selectedSchoolIds)
  );

  /**
   * Observable of all available schools
   */
  readonly allSchools$: Observable<School[]> = this.state$.pipe(
    map(state => state.allSchools)
  );

  /**
   * Observable of date range
   */
  readonly dateRange$: Observable<{ startDate: string; endDate: string }> = this.state$.pipe(
    map(state => ({ startDate: state.startDate, endDate: state.endDate }))
  );

  /**
   * Observable of date preset
   */
  readonly datePreset$: Observable<DateRangePreset> = this.state$.pipe(
    map(state => state.datePreset)
  );

  /**
   * Get current selected school IDs
   */
  get selectedSchoolIds(): number[] {
    return this.state$.value.selectedSchoolIds;
  }

  /**
   * Get current start date
   */
  get startDate(): string {
    return this.state$.value.startDate;
  }

  /**
   * Get current end date
   */
  get endDate(): string {
    return this.state$.value.endDate;
  }

  /**
   * Get current date preset
   */
  get datePreset(): DateRangePreset {
    return this.state$.value.datePreset;
  }

  /**
   * Get all available schools
   */
  get allSchools(): School[] {
    return this.state$.value.allSchools;
  }

  /**
   * Check if filter state is initialized
   */
  get isInitialized(): boolean {
    return this.state$.value.initialized;
  }

  /**
   * Initialize filter state by loading schools and selecting all by default.
   * Should be called once when the app loads or when navigating to a filtered view.
   *
   * @returns Observable that completes when initialization is done
   */
  initialize(): Observable<FilterState> {
    if (this.state$.value.initialized) {
      return of(this.state$.value);
    }

    return this.schoolApiService.getSchools(1, 200).pipe(
      map(response => {
        const schools = response.items;
        const allSchoolIds = schools.map(s => s.id);

        const newState: FilterState = {
          ...this.state$.value,
          allSchools: schools,
          selectedSchoolIds: allSchoolIds, // Select all by default
          initialized: true
        };

        this.state$.next(newState);
        return newState;
      }),
      catchError(error => {
        console.error('Failed to initialize filter state:', error);
        const newState: FilterState = {
          ...this.state$.value,
          initialized: true
        };
        this.state$.next(newState);
        return of(newState);
      })
    );
  }

  /**
   * Set selected school IDs
   */
  setSelectedSchools(schoolIds: number[]): void {
    this.state$.next({
      ...this.state$.value,
      selectedSchoolIds: schoolIds
    });
  }

  /**
   * Select all available schools
   */
  selectAllSchools(): void {
    const allIds = this.state$.value.allSchools.map(s => s.id);
    this.setSelectedSchools(allIds);
  }

  /**
   * Clear all selected schools
   */
  clearSelectedSchools(): void {
    this.setSelectedSchools([]);
  }

  /**
   * Set date range preset and calculate dates
   */
  setDatePreset(preset: DateRangePreset): void {
    if (preset === 'custom') {
      // For custom, don't change dates, just update preset
      this.state$.next({
        ...this.state$.value,
        datePreset: preset
      });
    } else {
      const { startDate, endDate } = calculateDateRange(preset);
      this.state$.next({
        ...this.state$.value,
        datePreset: preset,
        startDate,
        endDate
      });
    }
  }

  /**
   * Set custom date range
   */
  setCustomDateRange(startDate: string, endDate: string): void {
    this.state$.next({
      ...this.state$.value,
      datePreset: 'custom',
      startDate,
      endDate
    });
  }

  /**
   * Update schools list (e.g., after user's accessible schools change)
   */
  updateSchools(schools: School[], selectAll: boolean = true): void {
    const selectedIds = selectAll
      ? schools.map(s => s.id)
      : this.state$.value.selectedSchoolIds.filter(id =>
          schools.some(s => s.id === id)
        );

    this.state$.next({
      ...this.state$.value,
      allSchools: schools,
      selectedSchoolIds: selectedIds
    });
  }

  /**
   * Reset filters to default state (all schools selected, last 7 days)
   */
  resetToDefaults(): void {
    const allIds = this.state$.value.allSchools.map(s => s.id);
    const { startDate, endDate } = calculateDateRange('last7days');

    this.state$.next({
      ...this.state$.value,
      selectedSchoolIds: allIds,
      datePreset: 'last7days',
      startDate,
      endDate
    });
  }

  /**
   * Fully reset filter state (used on logout to clear user-specific data)
   */
  reset(): void {
    const { startDate, endDate } = calculateDateRange('last7days');

    this.state$.next({
      selectedSchoolIds: [],
      datePreset: 'last7days',
      startDate,
      endDate,
      allSchools: [],
      initialized: false
    });
  }
}
